import React from 'react';
import { CALENDAR_STATUS_KEYWORDS, NOTIFICATION_EVENT_TYPES, NOTIFICATION_BADGE_THRESHOLDS } from '../../shared/appConstants';
import arrowRightLine from '../../assets/images/arrow_right_line.svg';
import scrumIcon from '../../assets/images/scrum_icon.svg';
import { decodeHtmlContent, renderCommentContent, stripHtmlToPlainText } from '../../shared/htmlUtils';
import { ENDPOINTS } from '../../services';

const ACTION_LABEL_KEYS = {
  [NOTIFICATION_EVENT_TYPES.ASSIGNMENT]: 'notifications.actions.taskAssigned',
  [NOTIFICATION_EVENT_TYPES.STATUS_CHANGE]: 'notifications.actions.statusChanged',
  [NOTIFICATION_EVENT_TYPES.WORKLOG]: 'notifications.actions.worklogUpdated',
  [NOTIFICATION_EVENT_TYPES.COMMENT]: 'notifications.actions.commented',
  [NOTIFICATION_EVENT_TYPES.COMMENT_REPLY]: 'notifications.actions.replied',
  [NOTIFICATION_EVENT_TYPES.COMMENT_MENTION]: 'notifications.actions.mentioned',
  [NOTIFICATION_EVENT_TYPES.DESC_MENTION]: 'notifications.actions.mentioned',
  [NOTIFICATION_EVENT_TYPES.SPRINT_CREATED]: 'notifications.actions.sprintCreated'
};

const TARGET_LABEL_KEYS = {
  [NOTIFICATION_EVENT_TYPES.COMMENT_MENTION]: 'notifications.actions.inComment',
  [NOTIFICATION_EVENT_TYPES.DESC_MENTION]: 'notifications.actions.inDescription'
};

const isObject = (value) => value != null && typeof value === 'object' && !Array.isArray(value);

const splitAssignmentNames = (value = '') => (
  String(value || '')
    .split(',')
    .map((name) => name.trim())
    .filter(Boolean)
);

export const deriveAssignmentChange = (changes = {}) => {
  if (!isObject(changes)) {
    return {
      assignedToName: '',
      assignmentNames: [],
      actionLabelKey: ACTION_LABEL_KEYS[NOTIFICATION_EVENT_TYPES.ASSIGNMENT]
    };
  }

  const previousNames = splitAssignmentNames(changes.old_value);
  const nextNames = splitAssignmentNames(changes.new_value);
  const previousSet = new Set(previousNames);
  const nextSet = new Set(nextNames);
  const addedNames = nextNames.filter((name) => !previousSet.has(name));
  const removedNames = previousNames.filter((name) => !nextSet.has(name));

  if (removedNames.length > 0 && addedNames.length === 0) {
    return {
      assignedToName: removedNames.join(', '),
      assignmentNames: removedNames,
      actionLabelKey: 'notifications.actions.taskUnassigned'
    };
  }

  if (addedNames.length > 0) {
    return {
      assignedToName: addedNames.join(', '),
      assignmentNames: addedNames,
      actionLabelKey: ACTION_LABEL_KEYS[NOTIFICATION_EVENT_TYPES.ASSIGNMENT]
    };
  }

  const fallbackNames = nextNames.length > 0 ? nextNames : previousNames;
  return {
    assignedToName: fallbackNames.join(', '),
    assignmentNames: fallbackNames,
    actionLabelKey: ACTION_LABEL_KEYS[NOTIFICATION_EVENT_TYPES.ASSIGNMENT]
  };
};

export const deriveCommentChange = (changes = {}, fallbackText = '') => {
  if (!isObject(changes)) {
    return {
      text: String(fallbackText || '').trim(),
      actionLabelKey: '',
      isRemoved: false
    };
  }

  const newText = String(changes.new_value || '').trim();
  const oldText = String(changes.old_value || '').trim();

  if (!newText && oldText) {
    return {
      text: oldText,
      actionLabelKey: 'notifications.actions.commentRemoved',
      isRemoved: true
    };
  }

  return {
    text: newText || String(fallbackText || '').trim(),
    actionLabelKey: '',
    isRemoved: false
  };
};

const extractWorklogDuration = (rawValue = '') => {
  const normalized = String(rawValue || '').trim();
  if (!normalized) return '';

  const durationMatch = normalized.match(/(\d+)\s*h(?:\s+(\d+)\s*m)?\s*$/i);
  if (durationMatch) {
    const h = parseInt(durationMatch[1], 10);
    const m = parseInt(durationMatch[2] || '0', 10);
    if (h > 0 && m > 0) return `${h}h ${m}m`;
    if (h > 0) return `${h}h`;
    return `${m}m`;
  }

  const minsMatch = normalized.match(/(\d+)\s*m\s*$/i);
  if (minsMatch) {
    const totalMins = parseInt(minsMatch[1], 10);
    const h = Math.floor(totalMins / 60);
    const m = totalMins % 60;
    if (h > 0 && m > 0) return `${h}h ${m}m`;
    if (h > 0) return `${h}h`;
    return `${m}m`;
  }

  return normalized.replace(/\s+/g, ' ').trim();
};

const withWorklogSign = (value, sign = '+') => {
  const normalizedValue = String(value || '').trim().replace(/\s+/g, ' ');
  if (!normalizedValue) return '';
  if (/^[+-]\s*/.test(normalizedValue)) return normalizedValue;
  return `${sign} ${normalizedValue}`;
};

export const deriveWorklogChange = (changes = {}) => {
  if (!isObject(changes)) {
    return {
      worklogValue: '',
      actionLabelKey: ACTION_LABEL_KEYS[NOTIFICATION_EVENT_TYPES.WORKLOG]
    };
  }

  const hasNewValue = changes.new_value !== null && changes.new_value !== undefined && String(changes.new_value).trim() !== '';
  const hasOldValue = changes.old_value !== null && changes.old_value !== undefined && String(changes.old_value).trim() !== '';

  if (!hasNewValue && hasOldValue) {
    return {
      worklogValue: withWorklogSign(extractWorklogDuration(changes.old_value), '-'),
      actionLabelKey: 'notifications.actions.worklogRemoved'
    };
  }

  const sourceValue = hasNewValue ? changes.new_value : changes.old_value;
  return {
    worklogValue: withWorklogSign(extractWorklogDuration(sourceValue), '+'),
    actionLabelKey: ACTION_LABEL_KEYS[NOTIFICATION_EVENT_TYPES.WORKLOG]
  };
};

export const deriveWorklogValue = (changes = {}) => deriveWorklogChange(changes).worklogValue;

const buildActorName = (actor) => {
  if (!isObject(actor)) return '';
  const first = String(actor.first_name || '').trim();
  const last = String(actor.last_name || '').trim();
  const fullName = `${first} ${last}`.trim();
  return fullName || String(actor.name || '').trim() || String(actor.id || '').trim();
};

// Returns badge label for notification count
// Shows exact count up to 9, then 9+, 20+, 30+, 50+, 99+ for higher counts
export const getNotificationBadgeLabel = (count, t = null) => {
  if (!count || count < 1) return '';
  if (count >= NOTIFICATION_BADGE_THRESHOLDS.HUNDRED) {
    return typeof t === 'function' ? t('dashboard.notificationsBadge.ninetyNinePlus') : '99+';
  }
  if (count >= NOTIFICATION_BADGE_THRESHOLDS.FIFTY) {
    return typeof t === 'function' ? t('dashboard.notificationsBadge.fiftyPlus') : '50+';
  }
  if (count >= NOTIFICATION_BADGE_THRESHOLDS.THIRTY) {
    return typeof t === 'function' ? t('dashboard.notificationsBadge.thirtyPlus') : '30+';
  }
  if (count >= NOTIFICATION_BADGE_THRESHOLDS.TWENTY) {
    return typeof t === 'function' ? t('dashboard.notificationsBadge.twentyPlus') : '20+';
  }
  if (count >= NOTIFICATION_BADGE_THRESHOLDS.TEN) {
    return typeof t === 'function' ? t('dashboard.notificationsBadge.ninePlus') : '9+';
  }
  return String(count);
};

export const normalizeNotificationItem = (item = {}) => {
  if (!isObject(item)) {
    return {
      id: '',
      ticketId: '',
      ticketNumber: '',
      title: '',
      event_type: '',
      commentId: '',
      commentThreadId: '',
      replyToId: '',
      message: '',
      actorId: '',
      actorName: '',
      isRead: false,
      createdAt: '',
      updatedAt: '',
      mentionedUserName: null,
      mentionedUserId: null,
      changes: null,
      actionLabelKey: '',
      actionTargetTextKey: '',
      actionTargetText: '',
      fromStatus: '',
      toStatus: '',
      assignedToName: '',
      assignmentNames: [],
      worklogValue: '',
      commentText: '',
      replyText: '',
      replyToText: '',
      sprintStartDate: '',
      sprintEndDate: ''
    };
  }

  const eventType = item?.event_type;
  const changes = isObject(item?.changes) ? item.changes : null;
  const commentChange = deriveCommentChange(changes, item?.message || item?.parent_comment || '');
  const normalized = {
    id: item?.id,
    ticketId: item?.ticket_id || item?.ticketId || item?.sprint_id || '',
    ticketNumber: item?.ticket_number || item?.ticketNumber || item?.ticket_id || '',
    title: item?.ticket_title || item?.title || item?.sprint_name || item?.message || item?.ticket_id || '',
    event_type: eventType,
    commentId: item?.id || '',
    commentThreadId: item?.parent_comment_id || item?.id || '',
    replyToId: item?.parent_comment_id || '',
    message: item?.message || '',
    actorId: item?.actor_id || item?.actorId || item?.actor?.id || '',
    actorName: buildActorName(item?.actor),
    isRead: Boolean(item?.is_read ?? item?.isRead),
    createdAt: item?.createdAt || item?.created_at || '',
    updatedAt: item?.updatedAt || item?.updated_at || '',
    mentionedUserName: item?.mentioned_user_name || item?.mentionedUserName || null,
    mentionedUserId: item?.mentioned_user_id || item?.mentionedUserId || null,
    changes: changes,
    actionLabelKey: ACTION_LABEL_KEYS[eventType] || '',
    actionTargetTextKey: TARGET_LABEL_KEYS[eventType] || '',
    actionTargetText: '',
    fromStatus: '',
    toStatus: '',
    assignedToName: '',
    assignmentNames: [],
    worklogValue: '',
    commentText: '',
    replyText: '',
    replyToText: '',
    sprintStartDate: '',
    sprintEndDate: ''
  };

  if (eventType === NOTIFICATION_EVENT_TYPES.STATUS_CHANGE && changes) {
    normalized.fromStatus = changes.old_value || '';
    normalized.toStatus = changes.new_value || '';
  }

  if (eventType === NOTIFICATION_EVENT_TYPES.ASSIGNMENT && changes) {
    const assignmentChange = deriveAssignmentChange(changes);
    normalized.assignedToName = assignmentChange.assignedToName;
    normalized.assignmentNames = assignmentChange.assignmentNames;
    normalized.actionLabelKey = assignmentChange.actionLabelKey;
  }

  if (eventType === NOTIFICATION_EVENT_TYPES.WORKLOG && changes) {
    const worklogChange = deriveWorklogChange(changes);
    normalized.worklogValue = worklogChange.worklogValue;
    normalized.actionLabelKey = worklogChange.actionLabelKey;
  }

  if (eventType === NOTIFICATION_EVENT_TYPES.COMMENT) {
    normalized.commentText = commentChange.text;
    if (commentChange.actionLabelKey) {
      normalized.actionLabelKey = commentChange.actionLabelKey;
    }
  }

  if (eventType === NOTIFICATION_EVENT_TYPES.COMMENT_REPLY) {
    normalized.replyText = commentChange.text;
    normalized.commentText = commentChange.text;
    normalized.replyToText = item?.parent_comment || (!commentChange.isRemoved ? changes?.old_value || '' : '');
    if (commentChange.actionLabelKey) {
      normalized.actionLabelKey = commentChange.actionLabelKey;
    }
  }

  if (eventType === NOTIFICATION_EVENT_TYPES.SPRINT_CREATED) {
    normalized.actionTargetText = item?.sprint_name || item?.message || '';
    normalized.sprintStartDate = item?.sprint_start_date;
    normalized.sprintEndDate = item?.sprint_end_date;
  }

  if (eventType === NOTIFICATION_EVENT_TYPES.STANDUP_NOT_UPDATED_REMINDER || eventType === NOTIFICATION_EVENT_TYPES.TASK_STAGNATION_REMINDER) {
    normalized.actionTargetText = item?.message || '';
  }

  return normalized;
};

export const normalizeNotifications = (items = []) => (
  Array.isArray(items) ? items.map(normalizeNotificationItem) : []
);

export const groupNotificationsByTicket = (items = []) => {
  const groups = new Map();
  items.forEach((item) => {
    if (!isObject(item)) return;
    const ticketKey = item.ticketId || item.ticketNumber || item.id;
    if (!ticketKey) return;
    if (!groups.has(ticketKey)) {
      groups.set(ticketKey, {
        id: ticketKey,
        ticketNumber: item.ticketNumber || ticketKey,
        title: item.title || item.message || ticketKey,
        assignee: item.actorName
          ? { id: item.actorId || item.actorName, name: item.actorName }
          : null,
        notifications: []
      });
    }
    groups.get(ticketKey).notifications.push(item);
  });
  return Array.from(groups.values());
};

export const getInitial = (name = '') => {
  const trimmed = String(name).trim();
  const [firstChar = ''] = Array.from(trimmed);
  return firstChar ? firstChar.toUpperCase() : '?';
};

export const getStatusChipClassName = (status = '') => {
  const normalized = String(status).toLowerCase();
  if (CALENDAR_STATUS_KEYWORDS.TODO.some(keyword => normalized.includes(keyword))) {
    return 'notifications-status-chip--todo';
  }
  if (CALENDAR_STATUS_KEYWORDS.IN_PROGRESS.some(keyword => normalized.includes(keyword))) {
    return 'notifications-status-chip--in-progress';
  }
  if (CALENDAR_STATUS_KEYWORDS.REVIEW.some(keyword => normalized.includes(keyword))) {
    return 'notifications-status-chip--review';
  }
  if (CALENDAR_STATUS_KEYWORDS.DONE.some(keyword => normalized.includes(keyword))) {
    return 'notifications-status-chip--done';
  }
  if (CALENDAR_STATUS_KEYWORDS.BLOCKED.some(keyword => normalized.includes(keyword))) {
    return 'notifications-status-chip--blocked';
  }
  return 'notifications-status-chip--default';
};

export const getCommentThreadKey = (notification) => (
  notification?.commentThreadId
  || notification?.commentId
  || notification?.replyToId
  || notification?.replyToText
  || notification?.commentText
  || notification?.id
);

export const getNotificationActionDescriptor = (notification, { t, currentUserId, showCommentIcon = true } = {}) => {
  const actionLabel = notification?.actionLabel || (notification?.actionLabelKey ? t(notification.actionLabelKey) : '');
  const actionTargetText = notification?.actionTargetText || (notification?.actionTargetTextKey ? t(notification.actionTargetTextKey) : '');
  if (!notification?.actorName || !actionLabel) {
    return {
      fallbackMessage: notification?.message || '',
      actionLabel: '',
      actionTargetText: '',
      eventType: notification?.event_type,
      hasStatusChange: false,
      hasAssignment: false,
      assignmentNames: [],
      hasMultipleAssignees: false,
      hasWorklog: false,
      isComment: false,
      isCommentReply: false,
      isMention: false,
      isMentionedUser: false,
      showCommentIcon
    };
  }

  const eventType = notification?.event_type;
  const hasStatusChange = eventType === NOTIFICATION_EVENT_TYPES.STATUS_CHANGE
    && notification?.fromStatus
    && notification?.toStatus;
  const hasAssignment = eventType === NOTIFICATION_EVENT_TYPES.ASSIGNMENT
    && notification?.assignedToName;
  const assignmentNames = hasAssignment
    ? (Array.isArray(notification.assignmentNames) && notification.assignmentNames.length > 0
      ? notification.assignmentNames
      : splitAssignmentNames(notification.assignedToName))
    : [];
  const hasMultipleAssignees = assignmentNames.length > 1;
  const hasWorklog = eventType === NOTIFICATION_EVENT_TYPES.WORKLOG
    && notification?.worklogValue;
  const isComment = eventType === NOTIFICATION_EVENT_TYPES.COMMENT
    || eventType === NOTIFICATION_EVENT_TYPES.COMMENT_REPLY;
  const isCommentReply = eventType === NOTIFICATION_EVENT_TYPES.COMMENT_REPLY;
  const isMention = eventType === NOTIFICATION_EVENT_TYPES.COMMENT_MENTION
    || eventType === NOTIFICATION_EVENT_TYPES.DESC_MENTION;
  const isMentionedUser = isMention && notification?.mentionedUserId && currentUserId && String(notification.mentionedUserId) === String(currentUserId);
  const isSprintAction = eventType === NOTIFICATION_EVENT_TYPES.SPRINT_CREATED;

  return {
    fallbackMessage: '',
    actionLabel,
    actionTargetText,
    eventType,
    hasStatusChange,
    hasAssignment,
    assignmentNames,
    hasMultipleAssignees,
    hasWorklog,
    isComment,
    isCommentReply,
    isMention,
    isMentionedUser,
    showCommentIcon,
    isSprintAction
  };
};

export const renderActionLine = (notification, {
  isCompact = false,
  showCommentIcon = true,
  t,
  currentUserId,
  mentionUsers = []
} = {}) => {
  const descriptor = getNotificationActionDescriptor(notification, { t, currentUserId, showCommentIcon });
  if (descriptor.fallbackMessage) {
    return descriptor.fallbackMessage;
  }

  return (
    <span className={`notifications-message${isCompact ? ' notifications-message--compact' : ''}`}>
      <span className="notifications-actor-token">
        <span className="notifications-actor-box">{getInitial(notification.actorName)}</span>
        <span className="notifications-actor-name">{notification.actorName}</span>
      </span>
      <span className="notifications-action-chip">{descriptor.actionLabel}</span>
      {descriptor.isCommentReply && notification?.replyToText && (
        <>
          <span className="notifications-inline-label">on</span>
          {renderNotificationTextContent(notification.replyToText, {
            mentionUsers,
            className: 'notifications-comment-text'
          })}
        </>
      )}
      {descriptor.hasStatusChange && (
        <>
          <span className={`notifications-status-chip ${getStatusChipClassName(notification.fromStatus)}`}>
            {notification.fromStatus}
          </span>
          <img className="notifications-message-arrow" src={arrowRightLine} alt="to" />
          <span className={`notifications-status-chip ${getStatusChipClassName(notification.toStatus)}`}>
            {notification.toStatus}
          </span>
        </>
      )}
      {descriptor.hasAssignment && (
        <>
          <img className="notifications-message-arrow" src={arrowRightLine} alt="to" />
          {descriptor.hasMultipleAssignees ? (
            <span className="notifications-message-label notifications-message-label--assignees">
              {notification.assignedToName}
            </span>
          ) : (
            <span className="notifications-actor-token">
              <span className="notifications-actor-box">{getInitial(notification.assignedToName)}</span>
              <span className="notifications-actor-name">{notification.assignedToName}</span>
            </span>
          )}
        </>
      )}
      {descriptor.hasWorklog && (
        <>
          <img className="notifications-message-arrow" src={arrowRightLine} alt="to" />
          <span className={`notifications-status-chip ${getStatusChipClassName('')}`}>
            {notification.worklogValue}
          </span>
        </>
      )}
      {!descriptor.hasStatusChange && !descriptor.hasAssignment && !descriptor.hasWorklog && descriptor.actionTargetText && !descriptor.isMention && !descriptor.isSprintAction && (
        <span className="notifications-message-label">{descriptor.actionTargetText}</span>
      )}
      {descriptor.isSprintAction && (
        <span className="notifications-message-label notifications-sprint-action">
          <img src={scrumIcon} alt="sprint" className="notifications-sprint-icon" />
          <span>{descriptor.actionTargetText}</span>
          {notification?.sprintStartDate && notification?.sprintEndDate && (
            <span className="notifications-sprint-dates">
              ({new Date(notification.sprintStartDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              {' - '}
              {new Date(notification.sprintEndDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })})
            </span>
          )}
        </span>
      )}
      {descriptor.isMention && (
        <>
          {descriptor.isMentionedUser ? (
            <span className="notifications-message-label notifications-message-label--bold">you</span>
          ) : (
            <span className="notifications-actor-name notifications-actor-name--mentioned">@{notification.mentionedUserName}</span>
          )}
          {descriptor.actionTargetText && (
            <span className="notifications-message-label">{descriptor.actionTargetText}</span>
          )}
        </>
      )}
    </span>
  );
};

export const hasNotificationHtmlContent = (text = '') => (
  (() => {
    const value = String(text || '');
    const decoded = decodeHtmlContent(value);
    return /\[CODE_START_MARKER\]|\[CODE_END_MARKER\]|class=(?:"|&quot;)mention(?:"|&quot;)|data-user-id=|<\/?[a-z][\s\S]*?>/i
      .test(value)
      || /<\/?[a-z][\s\S]*?>/i.test(decoded);
  })()
);

const hasNotificationPreviewTextArtifacts = (text = '') => (
  /\[CODE_START_MARKER\]|\[CODE_END_MARKER\]/i.test(String(text || ''))
);

const buildMentionUserMap = (mentionUsers = []) => {
  const userMap = new Map();
  mentionUsers.forEach((user) => {
    if (!user) return;
    const id = user.id || user.user_id || user.value || user.email || user.name;
    const name = user.name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.label;
    if (!id || !name) return;
    userMap.set(String(id), name);
  });
  return userMap;
};

const replaceMentionMarkupWithText = (text, userMap) => (
  String(text || '').replace(
    /(?:&lt;|<)span\b[\s\S]*?class=(?:"mention"|&quot;mention&quot;)[\s\S]*?data-user-id=(?:"([^"]*)"|&quot;([^"&]+)&quot;)[\s\S]*?(?:&gt;|>)([\s\S]*?)(?:&lt;|<)\/span(?:&gt;|>)/gi,
    (_, rawId, encodedId, innerText) => {
      const mentionId = rawId || encodedId || '';
      const matchedName = mentionId ? userMap.get(String(mentionId)) : '';
      const fallbackText = decodeHtmlContent(innerText || '').trim();
      return matchedName ? `@${matchedName}` : fallbackText;
    }
  )
);

const getNotificationPreviewText = (text, mentionUsers = []) => {
  const preview = String(text || '').trim();
  if (!preview) return '';

  const userMap = buildMentionUserMap(mentionUsers);
  const segments = preview.split(/(\[CODE_START_MARKER\][\s\S]*?\[CODE_END_MARKER\])/g);

  return segments.map((segment) => {
    if (!segment) return '';

    const codeMatch = segment.match(/^\[CODE_START_MARKER\]([\s\S]*?)\[CODE_END_MARKER\]$/);
    if (codeMatch) {
      return codeMatch[1].replace(/\u00a0/g, ' ');
    }

    return stripHtmlToPlainText(replaceMentionMarkupWithText(segment, userMap));
  }).join('').trim();
};

const trimNotificationHtmlEdges = (html = '') => {
  if (!html || typeof DOMParser === 'undefined') return html;

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
    const container = doc.body.firstChild;
    if (!container) return html;

    const normalizeText = (value = '') => String(value || '').replace(/\u00a0/g, ' ');
    const hasMeaningfulNonTextContent = (node) => (
      node.nodeType === 1
      && Boolean(node.querySelector('img, video, audio, iframe, canvas, svg, table, pre, code'))
    );
    const isVisuallyEmptyElement = (node) => (
      node.nodeType === 1
      && !normalizeText(node.textContent).trim()
      && !hasMeaningfulNonTextContent(node)
    );

    const trimLeading = (node) => {
      while (node.firstChild) {
        const child = node.firstChild;
        if (child.nodeType === 3 && !normalizeText(child.textContent).trim()) {
          node.removeChild(child);
          continue;
        }
        if (child.nodeType === 1) {
          if (child.tagName === 'BR' || isVisuallyEmptyElement(child)) {
            node.removeChild(child);
            continue;
          }
          trimLeading(child);
          if (isVisuallyEmptyElement(child)) {
            node.removeChild(child);
            continue;
          }
        }
        break;
      }
    };

    const trimTrailing = (node) => {
      while (node.lastChild) {
        const child = node.lastChild;
        if (child.nodeType === 3 && !normalizeText(child.textContent).trim()) {
          node.removeChild(child);
          continue;
        }
        if (child.nodeType === 1) {
          if (child.tagName === 'BR' || isVisuallyEmptyElement(child)) {
            node.removeChild(child);
            continue;
          }
          trimTrailing(child);
          if (isVisuallyEmptyElement(child)) {
            node.removeChild(child);
            continue;
          }
        }
        break;
      }
    };

    trimLeading(container);
    trimTrailing(container);
    return container.innerHTML;
  } catch (error) {
    return html;
  }
};

const quoteNotificationHtml = (html = '') => {
  if (!html || typeof DOMParser === 'undefined') return `&ldquo;${html}&rdquo;`;

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
    const container = doc.body.firstChild;
    if (!container) return `&ldquo;${html}&rdquo;`;

    const walker = doc.createTreeWalker(container, NodeFilter.SHOW_TEXT);
    const textNodes = [];
    let node = walker.nextNode();
    while (node) {
      if (String(node.textContent || '').replace(/\u00a0/g, ' ').trim()) {
        textNodes.push(node);
      }
      node = walker.nextNode();
    }

    if (textNodes.length === 0) return `&ldquo;${html}&rdquo;`;

    const firstTextNode = textNodes[0];
    const lastTextNode = textNodes[textNodes.length - 1];
    firstTextNode.textContent = `“${String(firstTextNode.textContent || '').replace(/^\s+/, '')}`;
    lastTextNode.textContent = `${String(lastTextNode.textContent || '').replace(/\s+$/, '')}”`;

    return container.innerHTML;
  } catch (error) {
    return `&ldquo;${html}&rdquo;`;
  }
};

export const renderNotificationTextContent = (text, {
  mentionUsers = [],
  quoted = true,
  className = ''
} = {}) => {
  const normalizedText = String(text || '').trim();
  if (!normalizedText) return null;

  if (hasNotificationPreviewTextArtifacts(normalizedText)) {
    const previewText = getNotificationPreviewText(normalizedText, mentionUsers);
    const displayText = quoted ? `“${previewText}”` : previewText;
    return className ? <span className={className}>{displayText}</span> : displayText;
  }

  if (!hasNotificationHtmlContent(normalizedText)) {
    const displayText = quoted ? `“${normalizedText}”` : normalizedText;
    return className ? <span className={className}>{displayText}</span> : displayText;
  }

  const renderedHtml = trimNotificationHtmlEdges(renderCommentContent(normalizedText, mentionUsers));
  const wrappedHtml = quoted ? quoteNotificationHtml(renderedHtml) : renderedHtml;

  return <span className={className} dangerouslySetInnerHTML={{ __html: wrappedHtml }} />;
};

export const renderNotificationMessage = (notification, { isCompact = false, t, currentUserId, mentionUsers = [] } = {}) => {
  if (notification?.event_type === NOTIFICATION_EVENT_TYPES.REMINDER_TICKET) {
    return (
      <span className={`notifications-message${isCompact ? ' notifications-message--compact' : ''}`}>
        <a 
          href={`/${ENDPOINTS.TASK_URL(encodeURIComponent(notification.ticketNumber))}`} 
          target="_blank" 
          rel="noopener noreferrer" 
          onClick={(e) => e.stopPropagation()}
          className="notifications-message-label clickable-ticket-link"
        >
          {notification.ticketNumber}
        </a>
        <span className="notifications-message-label" style={{ marginLeft: 4 }}>
          {notification.title}
        </span>
        {Array.isArray(notification.missingUpdates) && notification.missingUpdates.length > 0 && (
          <span className="notifications-message-label" style={{ marginLeft: 4, color: 'inherit', opacity: 0.8 }}>
            (Missing: {notification.missingUpdates.map(u => String(u).charAt(0).toUpperCase() + String(u).slice(1).replace(/_/g, ' ')).join(' & ')})
          </span>
        )}
        {!notification.hideAssignees && Array.isArray(notification.assignees) && notification.assignees.length > 0 && (
          <span className="notifications-message-label" style={{ marginLeft: 8, display: 'inline-flex', alignItems: 'center', gap: '4px', verticalAlign: 'middle' }}>
            —
            {notification.assignees.map((assignee, idx) => (
              <span key={idx} className="notifications-actor-token">
                <span className="notifications-actor-box">{getInitial(assignee)}</span>
                <span className="notifications-actor-name">{assignee}</span>
              </span>
            ))}
          </span>
        )}
      </span>
    );
  }

  const actionLabel = notification?.actionLabel || (notification?.actionLabelKey ? t(notification.actionLabelKey) : '');
  if (!notification?.actorName || !actionLabel) {
    const rawMessage = notification?.actionTargetText || notification?.message || '';
    return renderNotificationTextContent(rawMessage, { mentionUsers, quoted: false }) || rawMessage;
  }

  const hasBulkChildren = Array.isArray(notification?.bulkActions) && notification.bulkActions.length > 0;
  return renderActionLine(notification, {
    isCompact,
    showCommentIcon: !hasBulkChildren,
    t,
    currentUserId,
    mentionUsers
  });
};

export const buildCommentSummary = (group) => {
  const notifications = Array.isArray(group?.notifications) ? group.notifications : [];
  if (notifications.length === 0) {
    return {
      replyNames: [],
      commentText: group?.title || '',
      hasReplies: false,
      showNames: false
    };
  }

  const rootComment = notifications.find(notification => (
    notification?.event_type === NOTIFICATION_EVENT_TYPES.COMMENT && notification?.commentText
  ));
  const fallbackNotification = notifications.find(notification => notification?.commentText || notification?.replyToText)
    || notifications[0];
  const commentText = rootComment?.commentText
    || fallbackNotification?.replyToText
    || fallbackNotification?.commentText
    || group?.title
    || '';

  const replyNotifications = notifications.filter(notification => (
    notification?.event_type === NOTIFICATION_EVENT_TYPES.COMMENT_REPLY
  ));
  const replyNames = Array.from(new Set(
    replyNotifications.map(notification => notification?.actorName).filter(Boolean)
  ));

  return {
    replyNames,
    commentText,
    hasReplies: replyNames.length > 0,
    showNames: replyNames.length > 1
  };
};
